import React, { useCallback, useState } from 'react';

import isNumber from 'utils/number.ts';

import { PlayerRef } from 'components/Player/types.ts';

type Params = {
    playerRef: React.RefObject<PlayerRef | null>;
    videoDuration: number;
    playerPausedByUser: boolean;
    isCatchup: boolean;
    isStartover: boolean;
    livePosition?: number;
};

type SkipParams = {
    // Exact position of the static stream to seek to.
    seekPosition?: number;
    // Negative offset from live position or timestamp in seconds of the dynamic stream.
    timeShiftOffset?: number;
};

export type OnSkipHandler = (p: SkipParams) => void;

export const SKIP_STEP = 20;

function useSkippings({
    playerRef,
    videoDuration,
    playerPausedByUser,
    isCatchup,
    isStartover,
    livePosition,
}: Params) {
    const [seeking, setSeeking] = useState(false);

    const handleSeeked = useCallback(() => {
        setSeeking(false);

        if (!playerPausedByUser) {
            playerRef.current?.play();
        } else {
            playerRef.current?.pause();
        }
    }, [playerPausedByUser, playerRef]);

    const handleResetSeeking = useCallback(() => {
        if (seeking) {
            setSeeking(false);
        }
    }, [seeking]);

    const handleSkip = useCallback<OnSkipHandler>(
        async ({ seekPosition, timeShiftOffset }: SkipParams) => {
            const seekDynamicStream =
                (isStartover || isCatchup) &&
                (await playerRef.current?.getIsLive());

            setSeeking(true);

            if (seekDynamicStream && isNumber(timeShiftOffset)) {
                playerRef.current?.timeShift(timeShiftOffset);
            } else if (isNumber(seekPosition)) {
                playerRef.current?.seek(
                    Math.min(
                        videoDuration,
                        seekPosition,
                        livePosition || Number.MAX_SAFE_INTEGER,
                    ),
                );
            }
        },
        [isStartover, isCatchup, playerRef, videoDuration, livePosition],
    );

    return {
        seeking,
        setSeeking,
        handleSeeked,
        handleSkip,
        handleResetSeeking,
    };
}

export default useSkippings;
